import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { X, Download, Share2, PlusSquare, Smartphone, Chrome, Compass, ExternalLink, CheckCircle2 } from 'lucide-react';

const DISMISS_KEY = 'verifypro_pwa_install_dismissed_at';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * PWA install prompt tailored for VerifyPro's field-agent console.
 *
 * Now also listens for a global "verifypro:install" custom event so a
 * persistent "Install app" button (see `InstallAppButton`) can re-open the
 * card at any time — even after the 7-day dismiss cooldown. Manual opens
 * bypass the cooldown so agents who dismissed once can still install later.
 *
 * Coverage matrix:
 *   Android Chrome/Edge/Samsung → real `beforeinstallprompt` flow (1-tap)
 *   iOS Safari                  → step-by-step Share → Add to Home Screen
 *   iOS Chrome/Firefox/Edge     → "Switch to Safari to install" (Apple limits)
 *   Desktop Chromium            → same as Android
 *   Already installed           → nothing (or a "you're installed" tick if
 *                                 manually opened while in standalone mode).
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null); // Android/Chromium event
  const [showIosSafari, setShowIosSafari] = useState(false);
  const [showIosChrome, setShowIosChrome] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // ---- UA / display-mode detection (memoised for reuse) ----
  const detect = useCallback(() => {
    const ua = window.navigator.userAgent || '';
    const isIos = /iPhone|iPad|iPod/i.test(ua);
    const isSafariCore = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
    const isIosChrome = isIos && /CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
    const standalone =
      window.matchMedia?.('(display-mode: standalone)')?.matches
      || window.navigator?.standalone === true;
    return { isIos, isSafariCore, isIosChrome, standalone };
  }, []);

  useEffect(() => {
    const { isIos, isSafariCore, isIosChrome, standalone } = detect();
    setIsStandalone(standalone);

    // If already installed / running as a PWA — nothing to auto-show.
    if (standalone) return;

    // Suppress the auto-open if the user dismissed this recently — but the
    // manual-trigger event listener below still works.
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const suppressed = at && Date.now() - at < DISMISS_COOLDOWN_MS;
    if (suppressed) setDismissed(true);

    // Android / desktop Chromium path
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS Safari path — show the step-by-step card (unless suppressed).
    if (isIos && isSafariCore && !suppressed) setShowIosSafari(true);
    // iOS Chrome / Firefox / Edge — show the "Switch to Safari" nudge
    // (unless suppressed).
    if (isIosChrome && !suppressed) setShowIosChrome(true);

    // Once installed we hide immediately.
    const onInstalled = () => {
      setDeferred(null);
      setShowIosSafari(false);
      setShowIosChrome(false);
      setDismissed(true);
      setIsStandalone(true);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [detect]);

  // ---- Manual re-open via global custom event ----
  useEffect(() => {
    const onManualOpen = () => {
      // Reset dismiss so the card is visible again.
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
      setDismissed(false);
      setManualOpen(true);
      // Re-run detection so iOS cards fire even if the very first mount
      // suppressed them.
      const { isIos, isSafariCore, isIosChrome, standalone } = detect();
      setIsStandalone(standalone);
      if (isIos && isSafariCore) setShowIosSafari(true);
      if (isIosChrome) setShowIosChrome(true);
      // Android — nothing to do; if `deferred` is present the button below
      // will trigger it, otherwise we render a "no install available"
      // fallback card.
    };
    window.addEventListener('verifypro:install', onManualOpen);
    return () => window.removeEventListener('verifypro:install', onManualOpen);
  }, [detect]);

  const doDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferred(null);
    setShowIosSafari(false);
    setShowIosChrome(false);
    setManualOpen(false);
    setDismissed(true);
  };

  const doInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch { /* user closed the sheet — fall through */ }
    setDeferred(null);
    setManualOpen(false);
  };

  // Nothing to show → nothing rendered.
  if (isStandalone && !manualOpen) return null;
  if (!manualOpen && dismissed) return null;
  if (!deferred && !showIosSafari && !showIosChrome && !manualOpen) return null;

  const containerCls =
    'fixed bottom-4 inset-x-3 sm:inset-x-auto sm:right-4 sm:w-[380px] z-[70] rounded-2xl bg-brand-navy text-white shadow-2xl border border-white/10 overflow-hidden';

  // ---- Already installed (manual reopen from within standalone) --------
  if (isStandalone) {
    return (
      <div className={containerCls} data-testid="pwa-install-standalone" role="dialog">
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-emerald-300/90">Installed</div>
            <div className="font-semibold mt-0.5">VerifyPro is already installed on this device.</div>
            <div className="text-xs text-white/70 mt-1">Open it from your home screen for the fastest experience.</div>
          </div>
          <button onClick={doDismiss} aria-label="Close" className="text-white/50 hover:text-white p-1 -m-1" data-testid="pwa-install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ---- Android / desktop Chromium card --------------------------------
  if (deferred) {
    return (
      <div className={containerCls} data-testid="pwa-install-prompt" role="dialog" aria-label="Install VerifyPro">
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-orange/15 flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-brand-orange" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-brand-orange/80">Install app</div>
            <div className="font-semibold mt-0.5">Add VerifyPro to your home screen</div>
            <div className="text-xs text-white/70 mt-1 leading-relaxed">
              Launch straight to your assignments, without a browser bar. Camera, GPS &amp; signature work exactly the same.
            </div>
          </div>
          <button onClick={doDismiss} aria-label="Dismiss" className="text-white/50 hover:text-white p-1 -m-1" data-testid="pwa-install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 pb-4 pt-1 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={doDismiss}
            className="flex-1 h-10 bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
            data-testid="pwa-install-later"
          >
            Not now
          </Button>
          <Button
            size="sm"
            onClick={doInstall}
            className="flex-1 h-10 bg-brand-orange hover:bg-brand-orange-hover text-white"
            data-testid="pwa-install-cta"
          >
            <Download className="w-4 h-4 mr-1.5" /> Install
          </Button>
        </div>
      </div>
    );
  }

  // ---- iOS Chrome / Firefox / Edge card — nudge to Safari -------------
  if (showIosChrome) {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    return (
      <div className={containerCls} data-testid="pwa-install-prompt-ios-chrome" role="dialog">
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
            <Chrome className="w-5 h-5 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-300/90">Switch to Safari</div>
            <div className="font-semibold mt-0.5 text-sm">Install requires Safari on iPhone</div>
            <div className="text-xs text-white/70 mt-1 leading-relaxed">
              Apple only allows installing apps from <b>Safari</b>. Chrome / Firefox on iPhone can&apos;t add the app to your home screen properly.
            </div>
          </div>
          <button onClick={doDismiss} aria-label="Dismiss" className="text-white/50 hover:text-white p-1 -m-1" data-testid="pwa-install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
        <ol className="px-4 pb-4 space-y-2 text-sm text-white/85">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-white/10 text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
            <span className="flex-1">Open <b>Safari</b> on your iPhone.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-white/10 text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
            <span className="flex-1">
              Paste this address:
              <button
                onClick={() => {
                  if (navigator.clipboard) navigator.clipboard.writeText(currentUrl);
                }}
                className="mt-1 w-full text-left text-[11px] font-mono bg-white/8 hover:bg-white/16 border border-white/10 rounded px-2 py-1.5 break-all"
                data-testid="pwa-copy-url"
                aria-label="Copy URL"
              >
                {currentUrl} <span className="text-brand-orange">📋 tap to copy</span>
              </button>
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-white/10 text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
            <span className="flex-1 flex flex-wrap items-center gap-1.5">
              In Safari, tap <Share2 className="w-4 h-4 inline text-brand-orange" /> <b>Share</b> → <PlusSquare className="w-4 h-4 inline text-brand-orange" /> <b>Add to Home Screen</b>.
            </span>
          </li>
        </ol>
      </div>
    );
  }

  // ---- iOS Safari card ------------------------------------------------
  if (showIosSafari) {
    return (
      <div className={containerCls} data-testid="pwa-install-prompt-ios" role="dialog" aria-label="Add VerifyPro to Home Screen">
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-orange/15 flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-brand-orange" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-brand-orange/80">Add to Home Screen</div>
            <div className="font-semibold mt-0.5">Install VerifyPro on your iPhone</div>
          </div>
          <button onClick={doDismiss} aria-label="Dismiss" className="text-white/50 hover:text-white p-1 -m-1" data-testid="pwa-install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
        <ol className="px-4 pb-4 space-y-2 text-sm text-white/85">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-white/10 text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
            <span className="flex-1 flex flex-wrap items-center gap-1.5">
              Tap the <Share2 className="w-4 h-4 inline text-brand-orange" /> <b>Share</b> icon at the bottom of Safari.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-white/10 text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
            <span className="flex-1 flex flex-wrap items-center gap-1.5">
              Scroll and choose <PlusSquare className="w-4 h-4 inline text-brand-orange" /> <b>Add to Home Screen</b>.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-white/10 text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
            <span className="flex-1">Tap <b>Add</b> in the top right — the VerifyPro icon appears on your home screen.</span>
          </li>
        </ol>
      </div>
    );
  }

  // ---- Manually opened but no install path available ------------------
  // (e.g., desktop browser without a `beforeinstallprompt` yet, or a
  // browser that just doesn't support PWA installation.)
  if (manualOpen) {
    return (
      <div className={containerCls} data-testid="pwa-install-unsupported" role="dialog">
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/8 flex items-center justify-center shrink-0">
            <Compass className="w-5 h-5 text-white/70" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-white/50">Install</div>
            <div className="font-semibold mt-0.5 text-sm">Not available on this browser</div>
            <div className="text-xs text-white/70 mt-1 leading-relaxed">
              Open this site in <b>Chrome</b> on Android, <b>Safari</b> on iPhone, or use your browser&apos;s menu <ExternalLink className="w-3 h-3 inline" /> → <i>Add to Home screen / Install app</i>.
            </div>
          </div>
          <button onClick={doDismiss} aria-label="Dismiss" className="text-white/50 hover:text-white p-1 -m-1" data-testid="pwa-install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}

/**
 * Persistent "Install app" trigger — drop into any header/toolbar.
 * Dispatches the `verifypro:install` custom event which the InstallPrompt
 * component listens for. Works on all browsers (falls back to a helpful
 * message on unsupported ones).
 */
export function InstallAppButton({ className = '', variant = 'ghost', testid = 'install-app-button' }) {
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const check = () => {
      setInstalled(
        window.matchMedia?.('(display-mode: standalone)')?.matches
        || window.navigator?.standalone === true,
      );
    };
    check();
    const mm = window.matchMedia?.('(display-mode: standalone)');
    mm?.addEventListener?.('change', check);
    return () => mm?.removeEventListener?.('change', check);
  }, []);

  // Don't render at all when already running as an installed PWA.
  if (installed) return null;

  const baseCls = variant === 'ghost'
    ? 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-white/80 hover:bg-white/10 hover:text-white transition-colors'
    : 'inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bg-brand-orange hover:bg-brand-orange-hover text-white transition-colors';

  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent('verifypro:install'))}
      className={`${baseCls} ${className}`}
      data-testid={testid}
      aria-label="Install VerifyPro app"
    >
      <Download className="w-3.5 h-3.5" strokeWidth={2} />
      <span>Install app</span>
    </button>
  );
}
